import { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

/* ══════════════════════════════════════════════════════════════════════════
 * AuthShell — escenario inmersivo "Spatial Glass" compartido por todas las
 * pantallas de auth (Login, Register, Confirm, ForgotPassword).
 *
 * Izquierda (≥1024px): escenario de marca con núcleo sináptico animado,
 * headline, claim rotativo y chips de métricas. Debajo de 1024px se oculta y
 * el panel derecho queda centrado.
 *
 * Derecha: tarjeta glass (`.sf-glass-strong`) con wordmark + `children`.
 * Todo tokenizado (`--sf-*` / `--primary` / `--foreground`) → theme-aware.
 * ════════════════════════════════════════════════════════════════════════ */

/* ── Estilos compartidos (inyectados una vez) ── */
const styleId = 'verix-auth-reinvented';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const s = document.createElement('style');
  s.id = styleId;
  s.textContent = `
    @keyframes vxFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes vxFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes vxPulseDot { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
    @keyframes vxSpin { to { transform: rotate(360deg); } }
    @keyframes vxSpinRev { to { transform: rotate(-360deg); } }
    @keyframes vxCorePulse { 0%,100% { opacity: 0.65; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }
    @keyframes vxAurora1 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(60px,50px) scale(1.12); } 66% { transform: translate(-40px,30px) scale(0.94); } }
    @keyframes vxAurora2 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(-70px,-40px) scale(1.1); } 66% { transform: translate(50px,-60px) scale(0.92); } }
    @keyframes vxClaimIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

    .vx-auth-grid { display: grid; grid-template-columns: 1fr; min-height: 100vh; }
    .vx-auth-stage { display: none; }
    @media (min-width: 1024px) {
      .vx-auth-grid { grid-template-columns: 1.05fr 0.95fr; }
      .vx-auth-stage { display: flex; }
    }

    .vx-core { position: relative; width: 400px; height: 400px; }
    .vx-ring { position: absolute; inset: 0; margin: auto; border-radius: 50%; border: 1px solid hsl(var(--primary) / 0.18); }
    .vx-ring-1 { width: 400px; height: 400px; border-top-color: hsl(var(--primary) / 0.75); border-right-color: hsl(var(--primary) / 0.35); animation: vxSpin 22s linear infinite; }
    .vx-ring-2 { width: 300px; height: 300px; border-bottom-color: hsl(var(--chart-2) / 0.6); border-left-color: hsl(var(--chart-2) / 0.3); animation: vxSpinRev 17s linear infinite; }
    .vx-ring-3 { width: 208px; height: 208px; border-top-color: hsl(var(--primary) / 0.55); animation: vxSpin 12s linear infinite; }
    .vx-orbit { position: absolute; inset: 0; margin: auto; border-radius: 50%; }
    .vx-orbit-1 { width: 400px; height: 400px; animation: vxSpin 16s linear infinite; }
    .vx-orbit-2 { width: 300px; height: 300px; animation: vxSpinRev 11s linear infinite; }
    .vx-node { position: absolute; top: -5px; left: 50%; transform: translateX(-50%); width: 11px; height: 11px; border-radius: 50%; background: hsl(var(--primary)); box-shadow: 0 0 18px hsl(var(--primary)), 0 0 6px hsl(var(--primary)); }
    .vx-node-sm { width: 8px; height: 8px; background: hsl(var(--chart-2)); box-shadow: 0 0 14px hsl(var(--chart-2)); }
    .vx-core-glow { position: absolute; inset: 0; margin: auto; width: 180px; height: 180px; border-radius: 50%; background: radial-gradient(circle, hsl(var(--primary) / 0.5), transparent 70%); filter: blur(14px); animation: vxCorePulse 4.5s ease-in-out infinite; }
    .vx-core-badge {
      position: absolute; inset: 0; margin: auto; width: 118px; height: 118px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      background: var(--sf-glass-bg); border: 1px solid var(--sf-glass-border);
      box-shadow: var(--sf-elev-3), var(--sf-accent-glow);
      backdrop-filter: blur(14px) saturate(150%); -webkit-backdrop-filter: blur(14px) saturate(150%);
    }

    .vx-input:focus { outline: none; border-color: hsl(var(--primary)); box-shadow: 0 0 0 3px hsl(var(--primary) / 0.15); }
    .vx-btn-primary { transition: transform .2s ease, box-shadow .2s ease, filter .2s ease; background: var(--sf-accent-grad); }
    .vx-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: var(--sf-accent-glow); filter: brightness(1.04); }
    .vx-btn-primary:active:not(:disabled) { transform: translateY(0); }
    .vx-btn-oauth { transition: transform .2s ease, box-shadow .2s ease, background .2s ease; }
    .vx-btn-oauth:hover { transform: translateY(-1px); box-shadow: var(--sf-elev-2); background: hsl(var(--muted) / 0.6); }

    @media (prefers-reduced-motion: reduce) {
      .vx-ring, .vx-orbit, .vx-core-glow, [data-vx-aurora] { animation: none !important; }
    }
  `;
  document.head.appendChild(s);
}

/* ── Contenido del escenario ── */
const CLAIMS = [
  'Contabilidad, nóminas y fiscal en una sola plataforma.',
  'VeriFactu y AEAT listos de serie, sin plugins.',
  'Agentes de IA que ejecutan, no solo sugieren.',
  'Multi-empresa y multi-divisa, con RGPD por diseño.',
];

const STAGE_METRICS = [
  { number: '13', label: 'Módulos ERP' },
  { number: 'AI', label: 'Agentes ARIA' },
  { number: 'AEAT', label: 'VeriFactu' },
  { number: '∞', label: 'Multi-empresa' },
];

/* ── VERIX wordmark (acento Spatial Glass) ── */
export function Wordmark({ size = 22 }) {
  return (
    <span className="sf-display" style={{ fontSize: size, letterSpacing: '-0.03em', fontWeight: 800 }}>
      <span style={{ color: 'hsl(var(--foreground))' }}>VER</span>
      <span
        style={{
          background: 'var(--sf-accent-grad)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        IX
      </span>
    </span>
  );
}

/* ── Estilos de campo reutilizables (token-based) ── */
export const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase',
  letterSpacing: '0.08em', marginBottom: 4,
};
export const inputStyle = {
  width: '100%', height: 44, paddingLeft: 44, paddingRight: 16, borderRadius: 10,
  border: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.45)',
  fontSize: 14, color: 'hsl(var(--foreground))',
  transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box',
};
export const iconStyle = {
  position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
  width: 18, height: 18, color: 'hsl(var(--primary))', pointerEvents: 'none',
};

/* ── Escenario de marca (izquierda) ── */
function BrandStage() {
  const [claimIdx, setClaimIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setClaimIdx((i) => (i + 1) % CLAIMS.length), 3800);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="vx-auth-stage"
      style={{ position: 'relative', flexDirection: 'column', justifyContent: 'space-between', padding: '44px 56px', overflow: 'hidden' }}
    >
      {/* Auroras sobre el spatial-field */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <div data-vx-aurora style={{ position: 'absolute', width: 640, height: 640, borderRadius: '50%', background: 'radial-gradient(circle, hsl(var(--primary) / 0.16) 0%, transparent 70%)', top: '-14%', left: '-8%', filter: 'blur(70px)', animation: 'vxAurora1 26s ease-in-out infinite' }} />
        <div data-vx-aurora style={{ position: 'absolute', width: 560, height: 560, borderRadius: '50%', background: 'radial-gradient(circle, hsl(var(--chart-5) / 0.14) 0%, transparent 70%)', bottom: '-18%', left: '10%', filter: 'blur(76px)', animation: 'vxAurora2 30s ease-in-out infinite' }} />
      </div>

      {/* Top: wordmark + status */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Wordmark size={24} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'hsl(var(--muted-foreground))', fontFamily: 'var(--font-mono, ui-monospace), monospace' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'hsl(var(--success))', animation: 'vxPulseDot 2s ease-in-out infinite', display: 'inline-block' }} />
          Sistema activo · eu-west-1
        </div>
      </div>

      {/* Center: núcleo sináptico */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, padding: '24px 0' }}>
        <div className="vx-core">
          <div className="vx-core-glow" />
          <div className="vx-ring vx-ring-1" />
          <div className="vx-ring vx-ring-2" />
          <div className="vx-ring vx-ring-3" />
          <div className="vx-orbit vx-orbit-1"><i className="vx-node" /></div>
          <div className="vx-orbit vx-orbit-2"><i className="vx-node vx-node-sm" /></div>
          <div className="vx-core-badge">
            <Sparkles style={{ width: 40, height: 40, color: 'hsl(var(--primary))' }} strokeWidth={1.5} />
          </div>
        </div>
      </div>

      {/* Bottom: headline + claim + métricas */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h1 className="sf-display" style={{ fontSize: 34, lineHeight: 1.12, color: 'hsl(var(--foreground))', margin: '0 0 14px', maxWidth: 480 }}>
          El primer ERP con<br />agentes de IA generativa
        </h1>
        <p key={claimIdx} style={{ fontSize: 15, color: 'hsl(var(--muted-foreground))', margin: '0 0 28px', minHeight: 24, maxWidth: 460, animation: 'vxClaimIn 0.5s ease both' }}>
          {CLAIMS[claimIdx]}
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {STAGE_METRICS.map((m) => (
            <div key={m.label} className="sf-chip" style={{ borderRadius: 12, padding: '10px 14px', display: 'flex', flexDirection: 'column', minWidth: 92 }}>
              <span className="sf-display sf-numeric" style={{ fontSize: 20, fontWeight: 700, color: 'hsl(var(--primary))', lineHeight: 1 }}>{m.number}</span>
              <span style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', marginTop: 4 }}>{m.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * AuthShell — layout inmersivo compartido.
 * @param {object}  props
 * @param {React.ReactNode} props.children  Contenido del panel derecho (formulario o estado).
 * @param {number}  [props.maxWidth=420]    Ancho máximo de la tarjeta glass.
 */
export default function AuthShell({ children, maxWidth = 420 }) {
  return (
    <div className="spatial-field vx-auth-grid" style={{ position: 'relative', overflow: 'hidden' }}>
      <BrandStage />

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px 24px', minHeight: '100vh', boxSizing: 'border-box' }}>
        <div className="sf-glass-strong" style={{ width: '100%', maxWidth, borderRadius: 22, padding: '28px 36px', animation: 'vxFadeIn 0.45s ease both' }}>
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <Wordmark size={28} />
          </div>
          {children}
        </div>

        <p style={{ marginTop: 16, fontSize: 12, color: 'hsl(var(--muted-foreground))', textAlign: 'center' }}>
          THEPINEAPPLE Corporation &copy; 2026
        </p>
      </div>
    </div>
  );
}
